import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, expand, map, reduce, switchMap } from 'rxjs/operators';
import { CaisseClient } from '../logiciel-caisse/logiciel-caisse-client.service';
import { ToastrService } from 'ngx-toastr';
import {
  CreateCaisseClientPayload,
  LogicielCaisseClientService
} from '../logiciel-caisse/logiciel-caisse-client.service';
import { LogicielCaisseAuthService } from '../logiciel-caisse/logiciel-caisse-auth.service';
import { readObservableError } from '../logiciel-caisse/logiciel-caisse-api.helper';
@Injectable({ providedIn: 'root' })
export class ClientCaisseSyncService {
  constructor(
    private readonly lcClientService: LogicielCaisseClientService,
    private readonly lcAuth: LogicielCaisseAuthService,
    private readonly toastr: ToastrService
  ) {}

  /** Après modification ERP : PUT client correspondant en caisse (best-effort). */
  syncAfterErpUpdate(
    formPayload: Record<string, unknown>,
    erpResponse: unknown
  ): Observable<void> {
    const erpBody =
      erpResponse && typeof erpResponse === 'object'
        ? ((erpResponse as { client?: Record<string, unknown> }).client ??
          (erpResponse as Record<string, unknown>))
        : {};
    const forCaisse: Record<string, unknown> = {
      ...erpBody,
      ...formPayload,
      emails: this.resolveEmailsForCaisse(formPayload, erpBody),
      telephones: formPayload['telephones'] ?? erpBody['telephones'],
      type: this.resolveClientTypeForCaisse(
        this.optionalClientString(formPayload['type']),
        this.optionalClientString(erpBody['type'])
      )
    };
    return this.syncUpdatedClientToCaisse(forCaisse).pipe(
      catchError((error) => {
        const detail = readObservableError(
          error,
          'La synchronisation caisse a échoué après la mise à jour ERP.'
        );
        this.toastr.warning(
          `Client mis à jour dans l’ERP, mais non synchronisé avec la caisse : ${detail}`,
          'Synchronisation partielle'
        );
        return of(undefined);
      })
    );
  }

  /** Après création ERP : POST client vers back-logiciel-caisse (best-effort + toast si échec). */
  syncAfterErpCreate(
    formPayload: Record<string, unknown>,
    erpResponse: unknown
  ): Observable<void> {
    const erpBody =
      erpResponse && typeof erpResponse === 'object'
        ? ((erpResponse as { client?: Record<string, unknown> }).client ??
          (erpResponse as Record<string, unknown>))
        : {};
    const forCaisse: Record<string, unknown> = {
      ...erpBody,
      ...formPayload,
      emails: this.resolveEmailsForCaisse(formPayload, erpBody),
      telephones: formPayload['telephones'] ?? erpBody['telephones'],
      // Type formulaire prioritaire (réponse ERP parfois vide / ENUM corrompu)
      type: this.resolveClientTypeForCaisse(
        this.optionalClientString(formPayload['type']),
        this.optionalClientString(erpBody['type'])
      )
    };
    return this.syncCreatedClientToCaisse(forCaisse).pipe(
      catchError((error) => {
        const detail = readObservableError(
          error,
          'La synchronisation avec le logiciel caisse a échoué.'
        );
        this.toastr.warning(
          `Client créé dans l’ERP, mais non synchronisé avec la caisse : ${detail}`,
          'Synchronisation partielle'
        );
        return of(undefined);
      })
    );
  }

  private syncUpdatedClientToCaisse(client: Record<string, unknown>): Observable<void> {
    const sendToCaisse = () => {
      const payload = this.mapErpClientToCaissePayload(client);
      return this.fetchAllCaisseClients().pipe(
        switchMap((list) => {
          const match = this.findCaisseClientForErpRecord(list, client);
          if (match?.id) {
            return this.lcClientService.updateClient(match.id, payload).pipe(map(() => undefined));
          }
          return throwError(
            () =>
              new Error(
                'Aucun client correspondant trouvé dans le logiciel caisse (PUT caisse ignoré).'
              )
          );
        })
      );
    };

    if (this.lcAuth.isReadyForNavigation()) {
      return sendToCaisse();
    }

    return this.lcAuth.bootstrap().pipe(switchMap(() => sendToCaisse()));
  }

  private fetchAllCaisseClients(): Observable<CaisseClient[]> {
    const pageSize = 100;
    return this.lcClientService.getClients({ page: 1, limit: pageSize }).pipe(
      expand((response) => {
        const currentPage = Number(response.pagination?.page) || 1;
        const totalPages = Number(response.pagination?.totalPages) || 1;
        if (currentPage >= totalPages) {
          return EMPTY;
        }
        return this.lcClientService.getClients({
          page: currentPage + 1,
          limit: pageSize
        });
      }),
      map((response) => response.items ?? []),
      reduce((all, pageItems) => all.concat(pageItems), [] as CaisseClient[])
    );
  }

  private normalizeIdentityPart(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  private caisseDisplayName(c: CaisseClient): string {
    return [c.prenom, c.nom].filter(Boolean).join(' ').trim() || String(c.nom ?? '').trim();
  }

  private collectErpEmails(client: Record<string, unknown>): string[] {
    const raw = Array.isArray(client['emails']) ? client['emails'] : [];
    return raw
      .map((item) => {
        if (typeof item === 'string') {
          return item.trim().toLowerCase();
        }
        if (item && typeof item === 'object') {
          return String((item as Record<string, unknown>)['email'] ?? '')
            .trim()
            .toLowerCase();
        }
        return '';
      })
      .filter(Boolean);
  }

  private collectErpPhones(client: Record<string, unknown>): string[] {
    const raw = Array.isArray(client['telephones']) ? client['telephones'] : [];
    return raw
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return '';
        }
        const row = item as Record<string, unknown>;
        const number = String(row['num_tel'] ?? '').replace(/\D+/g, '');
        const country = String(row['code_pays'] ?? '').replace(/\D+/g, '');
        if (!number) {
          return '';
        }
        return country && !number.startsWith(country) ? `${country}${number}` : number;
      })
      .filter(Boolean);
  }

  private normalizePhoneValue(value: unknown): string {
    return String(value ?? '').replace(/\D+/g, '');
  }

  private phonesMatch(a: string, b: string): boolean {
    if (!a || !b) {
      return false;
    }
    return a === b || a.endsWith(b) || b.endsWith(a);
  }

  private findCaisseClientForErpRecord(
    list: CaisseClient[],
    erp: Record<string, unknown>
  ): CaisseClient | undefined {
    const erpRef = this.normalizeIdentityPart(erp['identifiant_unique']);
    const erpCode = this.normalizeIdentityPart(erp['code_client']);
    const erpEmails = this.collectErpEmails(erp);
    const erpPhones = this.collectErpPhones(erp);
    const erpName = this.normalizeIdentityPart(
      [erp['nom_prenom'], erp['nom_entreprise']].filter(Boolean).join(' ')
    );

    for (const c of list || []) {
      const caisseRef = this.normalizeIdentityPart(c.identifiant_unique);
      if (
        erpRef &&
        caisseRef &&
        erpRef === caisseRef &&
        !erpRef.startsWith('lc-') &&
        !caisseRef.startsWith('lc-')
      ) {
        return c;
      }

      const caisseCode = this.normalizeIdentityPart(c.code_client);
      if (erpCode && caisseCode && erpCode === caisseCode) {
        return c;
      }

      const caisseEmail = String(c.email ?? '')
        .trim()
        .toLowerCase();
      if (
        caisseEmail &&
        erpEmails.length > 0 &&
        erpEmails.some((email) => email === caisseEmail)
      ) {
        return c;
      }

      const caissePhone = this.normalizePhoneValue(c.telephone);
      if (
        caissePhone &&
        erpPhones.length > 0 &&
        erpPhones.some((phone) => this.phonesMatch(phone, caissePhone))
      ) {
        return c;
      }

      const caisseName = this.normalizeIdentityPart(
        [this.caisseDisplayName(c), c.nom_entreprise].filter(Boolean).join(' ')
      );
      if (erpName && caisseName && erpName === caisseName) {
        return c;
      }
    }

    return undefined;
  }

  private syncCreatedClientToCaisse(client: Record<string, unknown>): Observable<void> {
    const sendToCaisse = () =>
      this.lcClientService.createClient(this.mapErpClientToCaissePayload(client)).pipe(
        map(() => undefined)
      );

    if (this.lcAuth.isReadyForNavigation()) {
      return sendToCaisse();
    }

    return this.lcAuth.bootstrap().pipe(switchMap(() => sendToCaisse()));
  }

  /** Aligné add-client (@erp/affichages) et back logiciel-caisse (Individuel | Professionnel | Négoce). */
  private normalizeClientType(type?: string | null): CreateCaisseClientPayload['type'] | '' {
    const value = (type ?? '').trim();
    if (!value) {
      return '';
    }
    const key = value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    if (key === 'individuel' || key === 'particulier' || key === 'materiel') {
      return 'Individuel';
    }
    if (
      key === 'professionnel' ||
      key === 'patente' ||
      key === 'entreprise' ||
      key === 'service'
    ) {
      return 'Professionnel';
    }
    if (key === 'negoce') {
      return 'Négoce';
    }
    const lettersOnly = key.replace(/[^a-z]/g, '');
    if (lettersOnly === 'negoce' || lettersOnly === 'ngoce') {
      return 'Négoce';
    }
    if (/^n.{0,6}goce$/i.test(value)) {
      return 'Négoce';
    }
    return '';
  }

  private resolveClientTypeForCaisse(
    ...candidates: (string | undefined)[]
  ): CreateCaisseClientPayload['type'] {
    for (const candidate of candidates) {
      const normalized = this.normalizeClientType(candidate);
      if (normalized) {
        return normalized;
      }
    }
    return 'Individuel';
  }

  private optionalClientString(value: unknown): string | undefined {
    const normalized = String(value ?? '').trim();
    return normalized || undefined;
  }

  /** Garde les emails du formulaire (strings) ou du détail ERP (objects avec .email). */
  private resolveEmailsForCaisse(
    formPayload: Record<string, unknown>,
    erpBody: Record<string, unknown>
  ): unknown {
    const fromForm = formPayload['emails'];
    if (Array.isArray(fromForm) && fromForm.length > 0) {
      return fromForm;
    }
    const fromErp = erpBody['emails'];
    if (Array.isArray(fromErp) && fromErp.length > 0) {
      return fromErp;
    }
    return fromForm ?? fromErp;
  }

  private mapErpClientToCaissePayload(client: Record<string, unknown>): CreateCaisseClientPayload {
    const rawEmails = Array.isArray(client['emails']) ? client['emails'] : [];
    const firstEmail = rawEmails[0];
    const email =
      typeof firstEmail === 'string'
        ? this.optionalClientString(firstEmail)
        : firstEmail && typeof firstEmail === 'object'
          ? this.optionalClientString((firstEmail as Record<string, unknown>)['email'])
          : undefined;

    const rawTelephones = Array.isArray(client['telephones']) ? client['telephones'] : [];
    const firstTelephone =
      rawTelephones[0] && typeof rawTelephones[0] === 'object'
        ? (rawTelephones[0] as Record<string, unknown>)
        : null;
    const phoneNumber = this.optionalClientString(firstTelephone?.['num_tel']);
    const countryCode = this.optionalClientString(firstTelephone?.['code_pays']);
    const telephone =
      phoneNumber && countryCode && !phoneNumber.startsWith(countryCode)
        ? `${countryCode}${phoneNumber}`
        : phoneNumber;

    const normalizedType = this.resolveClientTypeForCaisse(
      this.optionalClientString(client['type'])
    );
    const rawCivilite = this.optionalClientString(client['civilite'])?.toLowerCase();
    const civilite: CreateCaisseClientPayload['civilite'] =
      rawCivilite === 'femme' ? 'Femme' : rawCivilite === 'homme' ? 'Homme' : undefined;

    return {
      nom:
        this.optionalClientString(client['nom_prenom']) ||
        this.optionalClientString(client['nom_entreprise']) ||
        'Client ERP',
      email,
      telephone,
      type: normalizedType,
      civilite,
      identifiant_unique: this.optionalClientString(client['identifiant_unique']),
      nom_entreprise: this.optionalClientString(client['nom_entreprise']),
      numero_identification_fiscale: this.optionalClientString(
        client['numero_identification_fiscale']
      ),
      code_client: this.optionalClientString(client['code_client']),
      raison_social: this.optionalClientString(client['raison_social']),
      forme_juridique: this.optionalClientString(client['forme_juridique'])
    };
  }
}
